/// <amd-module name="Traceability/Changes/ChangesCard" />
import React = require("react");
import * as Resources from "Traceability/Changes/Resources";
import { Change } from "Traceability/Common/Contracts";
import { Utilities } from "Traceability/Common/Utilities";
import { TraceabilityBaseCard } from "Traceability/Common/TraceabilityBaseCard";
import { ITraceabilityCardProps } from "Traceability/Common/Contracts";
import { IVCNavigationService } from "VC/Common/Types/Navigation";
import { ObservableValue } from "VSS/Core/Observable";
import { localeFormat } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ICardTitleProps } from "VSSUI/Card";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { VssPersona } from "VSSUI/VssPersona";

export class ChangesCard extends TraceabilityBaseCard<Change> {
    public componentDidMount(): void {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._vcContributionPromise = contributionService.getContributionsAsync<any>(["ms.vss-code-web.common-content"]);
    }

    protected renderTable(context: IVssComponentContext, displayCount: number, cardTitleProps: ICardTitleProps): JSX.Element {
        // zero data length or data is not an array, then take zero as the length
        this._cardData.data = this._cardData.data || [];
        const dataLength = this._cardData.data!.length; // data null check done already
        const currentDisplayCount = Math.min(dataLength, displayCount);
        if (currentDisplayCount <= 0) {
            return <div>{this._cardData.innerErrorMessage ? Resources.NotAbleToFetchChanges : Resources.NoChangesText}</div>;
        }

        const ariaLabelForTable = !!cardTitleProps && cardTitleProps.text ? localeFormat(Resources.ChangesCardAriaLabel, cardTitleProps.text) : Resources.ChangesPageTitle;

        const commits = this._cardData.data!.slice(0, currentDisplayCount); // data null check done already
        const commitsProvider = new ArrayItemProvider<Change>(
            commits.map<Change>(change => {
                return {
                    ...change
                } as Change;
            })
        );

        return (
            <Table<Change>
                className="commits-table"
                key={commitsProvider.length}
                columns={this._columns}
                itemProvider={commitsProvider}
                rowHeight={64}
                spacerWidth={9}
                onActivate={this._onRowClicked}
                ariaLabel={ariaLabelForTable}                
            />
        );
    }

    private _onRowClicked = (event: any, row: ITableRow<Change>): void => {
        const artifactDetails = this._cardData.artifactDetails || {};
        const repo = artifactDetails && artifactDetails.repository || {};
        const version = artifactDetails && artifactDetails.artifactVersion || {};
        event.persist();
        this.vcNavigationService.then((service) => {
            const url = row.data.displayUri || service.getGitCommitUrl(row.data.id, repo.name || "", repo.projectId || version.projectId || "");
            // Handling Ctrl + Click
            if(!!event.ctrlKey){
                window.open(url, "_blank");
            }
            else{
                onClickFPS(this.context.pageContext, url, true, event);
            }
        });
    }

    private _renderCommitColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Change>, tableItem: Change): JSX.Element => {
        return (
            <TwoLineTableCell
                className="no-v-padding"
                key={"commits-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                line1={
                    <Tooltip overflowOnly={true} text={tableItem.message}>
                        <span className={"font-weight-semibold body-m text-ellipsis"}>{tableItem.message}</span>
                    </Tooltip>
                }
                line2={
                    <div className="flex-row" >
                        <VssPersona
                            size={"extra-small-plus"}
                            className="commit-persona-margin margin-right-8"
                            identityDetailsProvider={{
                                getIdentityImageUrl: (size: number): string => {
                                    return tableItem.author && tableItem.author._links && tableItem.author._links.avatar && tableItem.author._links.avatar.href;
                                },
                                getDisplayName: (): string => {
                                    return tableItem.author.displayName;
                                }
                            }}
                        />
                        <div className={"body-s secondary-text flex-row flex-center"}>
                            <span>{localeFormat(Resources.AuthoredFormat, tableItem.author.displayName)}&nbsp;</span>
                            {tableItem.timestamp && (
                                <Ago format={AgoFormat.Compact} date={tableItem.timestamp} />
                            )}
                        </div>
                    </div>
                }
            />
        );
    };

    private _renderShaColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Change>, tableItem: Change): JSX.Element => {
        return (
            <SimpleTableCell
                key={"commits-col-" + columnIndex}
                className={"body-m font-size-ms monospaced-text"}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                children={Utilities.getCommitShortId(tableItem.id)}
            />
        );
    }

    private _columns: ITableColumn<Change>[] = [
        {
            className: "commit-description-cell",
            id: "commit",
            name: Resources.CommitColumnName,
            width: new ObservableValue(-100),
            renderCell: this._renderCommitColumn
        },
        {
            id: "sha",
            name: Resources.ShaColumnName,
            width: new ObservableValue(240),
            renderCell: this._renderShaColumn
        }
    ];

    private get vcNavigationService(): Promise<IVCNavigationService> {
        if (!this._vcNavigationService) {
            this._vcNavigationService = this._vcContributionPromise.then(() => {
                return this.context.pageContext.getService<IVCNavigationService>("IVCNavigationService");
            });
        }

        return this._vcNavigationService;
    }

    private _vcContributionPromise: Promise<any>;
    private _vcNavigationService: Promise<IVCNavigationService> | undefined;
}
